import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// useDockDrag — arrastar as janelas ENCAIXADAS do Hub (Equipamento / Mochila /
// Ficha) para qualquer lugar da tela DURANTE a sessão. Ao fechar, a posição é
// descartada: a próxima abertura volta sempre à posição padrão (encaixada no
// canto, abaixo do painel da cidade).
// ─────────────────────────────────────────────────────────────────────────────

export interface DockPos { x: number; y: number; }

export function useDockDrag(open: boolean) {
  const [pos, setPos] = useState<DockPos | null>(null);
  const [dragging, setDragging] = useState(false);
  const off = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

  // Sempre que a janela fecha, esquece a posição arrastada.
  useEffect(() => {
    if (!open) { setPos(null); setDragging(false); }
  }, [open]);

  /** Handler para o cabeçalho da janela (o "pega" de arraste). */
  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    // Não arrastar quando o clique é num botão (fechar, abas, chips…).
    if ((e.target as HTMLElement).closest('button')) return;
    const host = (e.currentTarget as HTMLElement).closest('[data-dock-window]') as HTMLElement | null;
    if (!host) return;
    const r = host.getBoundingClientRect();
    off.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    setPos({ x: r.left, y: r.top });
    setDragging(true);
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => {
      const maxX = window.innerWidth - 90;
      const maxY = window.innerHeight - 44;
      setPos({
        x: Math.min(Math.max(4, e.clientX - off.current.dx), maxX),
        y: Math.min(Math.max(4, e.clientY - off.current.dy), maxY),
      });
    };
    const up = () => setDragging(false);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging]);

  const style: React.CSSProperties = pos
    ? { top: pos.y, left: pos.x, right: 'auto', maxHeight: `calc(100vh - ${pos.y}px - 12px)` }
    : {};

  return { style, dragging, onHeaderPointerDown };
}
